/**
 * Default Dashboard Configs
 *
 * These configs are used as fallbacks when BE doesn't return a config.
 * They define the default layout for each page.
 *
 * When BE is ready, these serve as templates/starting points for admins.
 */

import type { DashboardConfig } from '@/store/api/dashboardApi';

/**
 * Default config for Operator Proficiency page
 * Layout: Table (75%) | Sidebar with chart + card (25%)
 */
const operatorProficiencyConfig: DashboardConfig = {
  id: 'default-operator-proficiency',
  name: 'Operator Proficiency',
  orgId: 'default',
  pageId: 'operator-proficiency',
  cols: 12,
  rowHeight: 50,
  gap: 16,
  widgets: [
    {
      widgetId: 'data-table',
      instanceId: 'operator-proficiency-table',
      x: 0,
      y: 0,
      w: 9,
      h: 10,
      config: {
        dataSource: 'operator-proficiency',
        enableSearch: true,
        enableSorting: true,
      },
    },
    {
      widgetId: 'fleet-abuse-chart',
      instanceId: 'fleet-abuse-chart-1',
      x: 9,
      y: 0,
      w: 3,
      h: 4,
    },
    {
      widgetId: 'fleet-most-frequent',
      instanceId: 'fleet-most-frequent-1',
      x: 9,
      y: 4,
      w: 3,
      h: 6,
    },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  createdBy: 'system',
};

/**
 * Default config for Sites page
 * Layout: Full-width sites list
 */
const sitesConfig: DashboardConfig = {
  id: 'default-sites',
  name: 'Sites',
  orgId: 'default',
  pageId: 'sites',
  cols: 12,
  rowHeight: 50,
  gap: 16,
  widgets: [
    {
      widgetId: 'sites-list',
      instanceId: 'sites-list-main',
      x: 0,
      y: 0,
      w: 12,
      h: 10,
    },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  createdBy: 'system',
};

/**
 * Default config for Assets Preview page
 * Layout: Overview + Insights + Maintenance
 */
const assetsPreviewConfig: DashboardConfig = {
  id: 'default-assets-preview',
  name: 'Assets Preview',
  orgId: 'default',
  pageId: 'assets-preview',
  cols: 12,
  rowHeight: 50,
  gap: 16,
  widgets: [
    {
      widgetId: 'assets-overview',
      instanceId: 'assets-overview-main',
      x: 0,
      y: 0,
      w: 12,
      h: 8,
    },
    {
      widgetId: 'business-insights',
      instanceId: 'business-insights-main',
      x: 0,
      y: 8,
      w: 6,
      h: 6,
    },
    {
      widgetId: 'assets-maintenance',
      instanceId: 'assets-maintenance-main',
      x: 6,
      y: 8,
      w: 6,
      h: 6,
    },
  ],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  createdBy: 'system',
};

/**
 * All default configs mapped by pageId
 */
export const DEFAULT_DASHBOARD_CONFIGS: Record<string, DashboardConfig> = {
  'operator-proficiency': operatorProficiencyConfig,
  sites: sitesConfig,
  'assets-preview': assetsPreviewConfig,
};

/**
 * Get default config for a page
 */
export function getDefaultConfig(pageId: string): DashboardConfig | undefined {
  return DEFAULT_DASHBOARD_CONFIGS[pageId];
}

/**
 * Create a new empty dashboard config
 */
export function createEmptyConfig(pageId: string, name: string): DashboardConfig {
  return {
    id: `new-${pageId}-${Date.now()}`,
    name,
    orgId: 'default',
    pageId,
    cols: 12,
    rowHeight: 50,
    gap: 16,
    widgets: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'admin',
  };
}
